<template>
    <div>

        <PageHeader>
            {{ $t('dashboard.title') }}
            <template #side>
                <div class="d-flex gap-3">
                    <ButtonIcon icon="refresh-cw" variant="link" :disabled="loading" :icon-animation="iconAnimation" @click="reload" />
                    <TimeRangeDropdown v-model="timeRange" :disabled="loading" />
                </div>
            </template>
        </PageHeader>

        <div class="widget-stat-chart-wrapper">
            <WidgetStatChart
                class="widget-stat-chart"
                :title="$t('dashboard.impressions')"
                :value="$format.number(impressions.impressions)"
                :difference="$format.percent(percentDiff(impressions.impressions, impressions.impressions_compare))"
                :loading="$store.getters['userStats/getLoading']('impressionsStats')"
                :error="$store.getters['userStats/getError']('impressionsStats')"
                tooltip-formatter="round"
            />
            <WidgetStatChart
                class="widget-stat-chart"
                :title="$t('dashboard.clicks')"
                :value="$format.number(clicks.clicks)"
                :difference="$format.percent(percentDiff(clicks.clicks, clicks.clicks_compare))"
                :loading="$store.getters['userStats/getLoading']('clicksStats')"
                :error="$store.getters['userStats/getError']('clicksStats')"
                tooltip-formatter="round"
            />
            <WidgetStatChart
                class="widget-stat-chart"
                :title="$t('dashboard.ctr')"
                :value="$format.percent(clicks.clicks / impressions.impressions * 100)"
                :difference="$format.percent(percentDiff(clicks.clicks / impressions.impressions * 100, clicks.clicks_compare / impressions.impressions_compare * 100))"
                :loading="$store.getters['userStats/getLoading']('clicksStats') || $store.getters['userStats/getLoading']('impressionsStats')"
                :error="$store.getters['userStats/getError']('clicksStats') || $store.getters['userStats/getError']('impressionsStats')"
                tooltip-formatter="round"
            />
            <WidgetStatChart
                class="widget-stat-chart"
                :title="$t('dashboard.transactions')"
                :value="$format.number(orders.orders)"
                :difference="$format.percent(percentDiff(orders.orders, orders.orders_compare))"
                :loading="$store.getters['userStats/getLoading']('ordersStats')"
                :error="$store.getters['userStats/getError']('ordersStats')"
                tooltip-formatter="round"
            />
            <WidgetStatChart
                class="widget-stat-chart"
                :title="$t('dashboard.cr')"
                :value="$format.percent(orders.orders / clicks.clicks * 100)"
                :difference="$format.percent(percentDiff(orders.orders / clicks.clicks * 100, orders.orders_compare / clicks.clicks_compare * 100))"
                :loading="$store.getters['userStats/getLoading']('ordersStats') || $store.getters['userStats/getLoading']('clicksStats')"
                :error="$store.getters['userStats/getError']('ordersStats') || $store.getters['userStats/getError']('clicksStats')"
                tooltip-formatter="round"
            />
            <WidgetStatChart
                class="widget-stat-chart"
                :title="$t('dashboard.quantity')"
                :value="$format.number(sales.quantity)"
                :difference="$format.percent(percentDiff(sales.quantity, sales.quantity_compare))"
                :loading="$store.getters['userStats/getLoading']('salesStats')"
                :error="$store.getters['userStats/getError']('salesStats')"
                tooltip-formatter="round"
            />
            <WidgetStatChart
                class="widget-stat-chart"
                :title="$t('dashboard.salesAmountOwn')"
                :value="$format.currency(sales.amount)"
                :difference="$format.percent(percentDiff(sales.amount, sales.amount_compare))"
                :loading="$store.getters['userStats/getLoading']('salesStats')"
                :error="$store.getters['userStats/getError']('salesStats')"
                tooltip-formatter="round"
            />
            <WidgetStatChart
                class="widget-stat-chart"
                :title="$t('dashboard.amountOther')"
                :value="$format.currency(sales.amount_other)"
                :difference="$format.percent(percentDiff(sales.amount_other, sales.amount_other_compare))"
                :loading="$store.getters['userStats/getLoading']('salesStats')"
                :error="$store.getters['userStats/getError']('salesStats')"
                tooltip-formatter="round"
            />
            <WidgetStatChart
                class="widget-stat-chart"
                :title="$t('dashboard.amountUnknown')"
                :value="$format.currency(sales.amount_unknown)"
                :difference="$format.percent(percentDiff(sales.amount_unknown, sales.amount_unknown_compare))"
                :loading="$store.getters['userStats/getLoading']('salesStats')"
                :error="$store.getters['userStats/getError']('salesStats')"
                tooltip-formatter="round"
            />
        </div>

        <Chart
            card
            class="mt-3"
            :height="400"
            :series="[]"
            :options="trafficChartOptions"
            :header="$t('dashboard.traffic')"
            :error="$store.getters['userStats/getError']('trafficChart')"
            :loading="$store.getters['userStats/getLoading']('trafficChart')"
        >
            <template #nav>
                <Multiselect
                    v-model="campaigns"
                    :fetch-options-function="fetchCampaigns"
                    :placeholder="$t('selectCampaign')"
                    searchable
                    track-by="id"
                    label="name"
                    select-label=""
                    :disabled="$store.getters['userStats/getLoading']('trafficChart')"
                    show-checkboxes
                    show-selected
                    multiple
                    :close-on-select="false"
                    :clear-on-select="false"
                    class="width-min-250"
                    @change="fetchTrafficChart()"
                />
            </template>
        </Chart>

        <Chart
            card
            class="mt-3"
            :height="400"
            :series="[]"
            :options="salesChartOptions"
            :header="$t('dashboard.sales')"
            :error="$store.getters['userStats/getError']('salesChart')"
            :loading="$store.getters['userStats/getLoading']('salesChart')"
        />
    </div>
</template>



<script>
    import DashboardMixin from '@/views/common/dashboard/index.vue';
    import TimeRangeDropdown from '@/components/common/TimeRangeDropdown.vue';
    import { generateChartData } from '@/utils/chart';
    import Chart from '@/components/common/Chart.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import cloneDeep from 'lodash/cloneDeep';

    export default {
        name: 'ProducerDashboard',
        mixins: [DashboardMixin],
        components: { TimeRangeDropdown, Chart, Multiselect },
        data() {
            return {
                requestCampaigns: this.$req('buyboxes/AvailableCampaigns'),
                campaigns: [],
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            loading() {
                return (
                    this.$store.getters['userStats/getLoading']('impressionsStats') ||
                    this.$store.getters['userStats/getLoading']('clicksStats') ||
                    this.$store.getters['userStats/getLoading']('ordersStats') ||
                    this.$store.getters['userStats/getLoading']('salesStats') ||
                    this.$store.getters['userStats/getLoading']('trafficChart') ||
                    this.$store.getters['userStats/getLoading']('salesChart')
                );
            },
            iconAnimation() {
                return this.loading ? 'spin' : undefined;
            },
            chartOverview() {
                return {};
            },
            trafficChartOptions() {
                const chartData = this.trafficChart.reduce(
                    (acc, item) => {
                        acc.impressions.push([item.date, item.impressions]);
                        acc.clicks.push([item.date, item.clicks]);
                        return acc;
                    },
                    { impressions: [], clicks: [] }
                );
                return {
                    series: [
                        {
                            name: this.$t('dashboard.clicks'),
                            type: 'line',
                            data: generateChartData(this.timeRange, chartData.clicks, this.groupBy),
                        },
                        {
                            name: this.$t('dashboard.offersDisplay'),
                            type: 'bar',
                            data: generateChartData(this.timeRange, chartData.impressions, this.groupBy),
                        },
                    ],
                    yaxis: [
                        {
                            title: {
                                text: this.$t('dashboard.clicks'),
                            },
                        },
                        {
                            opposite: true,
                            title: {
                                text: this.$t('dashboard.offersDisplay'),
                            },
                        },
                    ],
                };
            },
            salesChartOptions() {
                const chartData = this.salesChart.reduce(
                    (acc, item) => {
                        acc.amount.push([item.date, item.amount]);
                        acc.quantity.push([item.date, item.quantity]);
                        return acc;
                    },
                    { amount: [], quantity: [] }
                );
                return {
                    series: [
                        {
                            name: this.$t('dashboard.quantity'),
                            type: 'line',
                            data: generateChartData(this.timeRange, chartData.quantity, this.groupBy),
                        },
                        {
                            name: this.$t('dashboard.salesAmount'),
                            type: 'bar',
                            data: generateChartData(this.timeRange, chartData.amount, this.groupBy),
                        },
                    ],
                    yaxis: [
                        {
                            title: {
                                text: this.$t('dashboard.quantity'),
                            },
                        },
                        {
                            opposite: true,
                            title: {
                                text: this.$t('dashboard.salesAmount'),
                            },
                        },
                    ],
                };
            },
        },
        watch: {
            spaceId: {
                handler: 'fetchData',
            },
        },
        methods: {
            fetchData(params = {}) {
                this.fetchImpressionsStats(params);
                this.fetchClicksStats(params);
                this.fetchOrdersStats(params);
                this.fetchSalesStats(params);
                this.fetchTrafficChart(params);
                this.fetchSalesChart(params);
            },
            reload() {
                const params = { cache: false };
                this.fetchData(params);
            },
            fetchTrafficChart(params = { cache: false }) {
                const apiParams = cloneDeep(this.apiParams);
                if (this.campaigns?.length) apiParams.requestParams.campaigns = this.campaigns.map(item => item.id).join(',');
                this.$store.dispatch('userStats/fetchTrafficChart', { ...apiParams, ...params });
            },
            fetchCampaigns() {
                const id = this.$store.getters['plugin/getProducerPlugin'].id;
                return this.requestCampaigns.get({ urlParams: { id, v: 1 } });
            },
        },
    };
</script>
